// src/pages/WorkoutPage.js
import { useEffect, useMemo, useCallback, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import WorkoutList from "../components/WorkoutList";
import MuscleMap from "../components/MuscleMap";
import "./WorkoutPage.css";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

function ymdToday() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(
    t.getDate()
  ).padStart(2, "0")}`;
}

function prettyDate(ymd) {
  const d = new Date(`${ymd}T00:00:00`);
  const weekday = d.toLocaleDateString(undefined, { weekday: "long" });
  const month = d.toLocaleDateString(undefined, { month: "short" });
  const day = d.getDate();
  return `${weekday}, ${month} ${day}`;
}

function shiftYmd(ymd, deltaDays) {
  const d = new Date(`${ymd}T00:00:00`);
  d.setDate(d.getDate() + deltaDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

// helper: stable “key” for a Set, so we can compare quickly
function setKey(s) {
  if (!s || s.size === 0) return "";
  return [...s].slice().sort().join("|");
}

export default function WorkoutPage({ user }) {
  const location = useLocation();

  const [activeDay, setActiveDay] = useState(ymdToday());
  const [primary, setPrimary] = useState(() => new Set());
  const [secondary, setSecondary] = useState(() => new Set());

  // Track last applied highlight keys, so we don't set state redundantly
  const lastPrimaryKey = useRef("");
  const lastSecondaryKey = useRef("");

  // ✅ Stable callback identity + skip state updates if unchanged
  const handleHighlightUpdate = useCallback(({ primary, secondary }) => {
    const p = primary instanceof Set ? primary : new Set(primary || []);
    const s = secondary instanceof Set ? secondary : new Set(secondary || []);

    const pk = setKey(p);
    const sk = setKey(s);

    // Skip redundant updates (prevents render loop)
    if (pk === lastPrimaryKey.current && sk === lastSecondaryKey.current) return;

    lastPrimaryKey.current = pk;
    lastSecondaryKey.current = sk;

    setPrimary(p);
    setSecondary(s);
  }, []);

  // Logs
  useEffect(() => {
    console.log("[WorkoutPage] MOUNT", {
      routerPath: location.pathname,
      windowPath: window.location.pathname,
    });
    return () => console.log("[WorkoutPage] UNMOUNT");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log(
      "[WorkoutPage] router location changed:",
      location.pathname,
      "| window:",
      window.location.pathname
    );
  }, [location.pathname]);

  // Optional: memoize props to MuscleMap to avoid re-renders from new Set identity
  const mmPrimary = useMemo(() => primary, [primary]);
  const mmSecondary = useMemo(() => secondary, [secondary]);

  return (
    <>
      <Navbar user={user} tab="workouts" />

      <div className="page-wrapper workout-page">
        <div className="page-container">
          <main className="wp-page">
            <div className="wp-panel">
              <div className="wp-top">
                <MuscleMap primary={mmPrimary} secondary={mmSecondary} mode="track" />
              </div>

              <div className="wp-day" aria-live="polite">
                <button
                  className="wp-dayArrow"
                  type="button"
                  onClick={() => setActiveDay((d) => shiftYmd(d, -1))}
                  aria-label="Previous day"
                >
                  <FiChevronLeft />
                </button>

                <div className="wp-dayCenter">
                  <div className="wp-dayDate">{prettyDate(activeDay)}</div>
                </div>

                <button
                  className="wp-dayArrow"
                  type="button"
                  onClick={() => setActiveDay((d) => shiftYmd(d, +1))}
                  aria-label="Next day"
                >
                  <FiChevronRight />
                </button>
              </div>

              <div className="wp-bottom">
                <WorkoutList
                  key={activeDay}
                  currentYmd={activeDay}
                  onHighlightUpdate={handleHighlightUpdate}
                />
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}